import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { api } from '../../services/api';

// ── Crop helper — draws cropped pixels onto a canvas and returns a Blob ───────
async function getCroppedBlob(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.92)
  );
}

// ── Crop modal ─────────────────────────────────────────────────────────────────
const CropModal: React.FC<{
  imageSrc: string;
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}> = ({ imageSrc, onConfirm, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => setCroppedArea(pixels), []);

  const handleConfirm = async () => {
    if (!croppedArea) return;
    const blob = await getCroppedBlob(imageSrc, croppedArea);
    onConfirm(blob);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-md shadow-2xl overflow-hidden" style={{ background: '#131313', border: '1px solid rgba(217,48,37,0.25)', borderRadius: '4px' }}>
        <div className="px-4 pt-4 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '13px', color: '#EEEAD5', marginBottom: '2px' }}>Crop Photo</h3>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#888874' }}>Drag to pan · Pinch or scroll to zoom</p>
        </div>

        {/* Crop area */}
        <div className="relative w-full" style={{ height: 320 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { background: '#0A0A0A' },
              cropAreaStyle: { border: '2px solid rgba(217,48,37,0.8)', boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)' },
            }}
          />
        </div>

        {/* Zoom slider */}
        <div className="px-4 py-3 flex items-center gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <span style={{ color: '#888874', fontSize: '11px', width: '32px', fontFamily: "'DM Sans', sans-serif" }}>Zoom</span>
          <input
            type="range" min={1} max={3} step={0.01}
            value={zoom} onChange={e => setZoom(Number(e.target.value))}
            className="flex-1 accent-[#D93025]"
          />
          <span style={{ color: '#888874', fontSize: '11px', width: '32px', textAlign: 'right', fontFamily: "'DM Sans', sans-serif" }}>{zoom.toFixed(1)}×</span>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-2 justify-end">
          <button onClick={onCancel} style={{ padding: '6px 12px', fontSize: '12px', color: '#888874', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 600, background: '#D93025', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
          >
            Use Photo
          </button>
        </div>
      </div>
    </div>
  );
};

type SourceName = 'SSCC' | 'BBOR' | 'SORC';
type StatusFilter = 'FINISHED' | 'DQ' | 'DNF' | 'DNS' | 'NT';

const SOURCES: SourceName[] = ['SSCC', 'BBOR', 'SORC'];
const STATUS_FILTERS: Array<{ label: string; value: '' | StatusFilter }> = [
  { label: 'All statuses', value: '' },
  { label: 'Finished', value: 'FINISHED' },
  { label: 'DQ', value: 'DQ' },
  { label: 'DNF', value: 'DNF' },
  { label: 'DNS', value: 'DNS' },
  { label: 'NT', value: 'NT' },
];

type HiddenRacer = {
  racer_id: string;
  canonical_name: string;
  note?: string | null;
};

type WomenReference = {
  id: string;
  source_system: string;
  external_id: string;
  canonical_name: string;
  alias_names: string[];
  notes?: string | null;
  is_active: boolean;
};

const PitWallAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedSource, setSelectedSource] = useState<SourceName>('SSCC');
  const [eventUrl, setEventUrl] = useState('');
  const [limit, setLimit] = useState(10);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [refSourceSystem, setRefSourceSystem] = useState('manual');
  const [refExternalId, setRefExternalId] = useState('');
  const [refCanonicalName, setRefCanonicalName] = useState('');
  const [refAliasNames, setRefAliasNames] = useState('');
  const [refNotes, setRefNotes] = useState('');
  const [editingReferenceId, setEditingReferenceId] = useState<string | null>(null);
  const [editCanonicalName, setEditCanonicalName] = useState('');
  const [editAliasNames, setEditAliasNames] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [womenSearch, setWomenSearch] = useState('');
  const [womenOnlyActive, setWomenOnlyActive] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [racerQuery, setRacerQuery] = useState('');
  const [racerStatusFilter, setRacerStatusFilter] = useState<'' | StatusFilter>('');
  const [editingPhotoRacerId, setEditingPhotoRacerId] = useState<string | null>(null);
  const [photoUrlDraft, setPhotoUrlDraft] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoTab, setPhotoTab] = useState<'url' | 'upload'>('upload');
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropTargetRacerId, setCropTargetRacerId] = useState<string | null>(null);

  // Merge UI state
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeQueue, setMergeQueue] = useState<Array<{ id: string; name: string }>>([]);
  const [mergeCanonicalId, setMergeCanonicalId] = useState<string | null>(null);
  const [mergeNameOverride, setMergeNameOverride] = useState('');
  const [mergeConfirming, setMergeConfirming] = useState(false);

  const [hideSearch, setHideSearch] = useState('');
  const [hideNote, setHideNote] = useState('');

  const { data: runsResponse, isLoading: isRunsLoading } = useQuery({
    queryKey: ['pitwallScrapeRuns', selectedSource],
    queryFn: () => api.admin.pitwall.getRuns(selectedSource, 25),
  });

  const runs = useMemo(() => runsResponse?.results ?? [], [runsResponse]);

  const { data: runDetail, isLoading: isRunDetailLoading } = useQuery({
    queryKey: ['pitwallScrapeRunDetail', selectedRunId],
    queryFn: () => api.admin.pitwall.getRunDetails(selectedRunId as string),
    enabled: !!selectedRunId,
  });

  const { data: womenReferences, isLoading: isWomenReferencesLoading } = useQuery<WomenReference[]>({
    queryKey: ['pitwallWomenReferences', womenSearch, womenOnlyActive],
    queryFn: () =>
      api.admin.pitwall.getWomenReferences({
        q: womenSearch.trim() || undefined,
        onlyActive: womenOnlyActive,
        limit: 500,
      }),
  });

  const {
    data: racerSearchResponse,
    isLoading: isRacerSearchLoading,
    isFetching: isRacerSearchFetching,
  } = useQuery({
    queryKey: ['pitwallRacerSearch', racerQuery, racerStatusFilter],
    queryFn: () =>
      api.admin.pitwall.searchRacers(racerQuery.trim(), {
        statusFilter: racerStatusFilter || undefined,
        limit: 50,
      }),
    enabled: racerQuery.trim().length >= 2,
  });

  const bootstrapSources = useMutation({
    mutationFn: () => api.admin.pitwall.bootstrapSources(),
    onSuccess: () => {
      setFeedbackMessage('Sources bootstrapped successfully.');
      queryClient.invalidateQueries({ queryKey: ['pitwallScrapeRuns'] });
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to bootstrap sources.');
    },
  });

  const runManualScrape = useMutation({
    mutationFn: () =>
      api.admin.pitwall.runManualScrape(selectedSource, {
        event_url: eventUrl.trim() || null,
        limit,
      }),
    onSuccess: (data: any) => {
      setFeedbackMessage(`Manual scrape complete. Status: ${data?.status || 'unknown'}`);
      queryClient.invalidateQueries({ queryKey: ['pitwallScrapeRuns'] });
      if (data?.run_id) {
        setSelectedRunId(data.run_id);
      }
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to run manual scrape.');
    },
  });

  const runSmokeChecks = useMutation({
    mutationFn: async () => {
      const [mainHealth, pitwallHealth, pitwallRuns] = await Promise.all([
        api.checkHealth(),
        api.admin.pitwall.checkHealth(),
        api.admin.pitwall.getRuns(undefined, 1),
      ]);
      return { mainHealth, pitwallHealth, pitwallRuns };
    },
    onSuccess: (data: any) => {
      const pitwallOk = data?.pitwallHealth?.status === 'ok';
      const mainOk = data?.mainHealth === true;
      const runsCount = data?.pitwallRuns?.count ?? 0;
      setFeedbackMessage(
        `Smoke checks: main=${mainOk ? 'ok' : 'fail'} pitwall=${pitwallOk ? 'ok' : 'fail'} runs=${runsCount}`
      );
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Smoke checks failed.');
    },
  });

  const createWomenReference = useMutation({
    mutationFn: () =>
      api.admin.pitwall.createWomenReference({
        source_system: refSourceSystem.trim() || 'manual',
        external_id: refExternalId.trim(),
        canonical_name: refCanonicalName.trim(),
        alias_names: refAliasNames
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean),
        notes: refNotes.trim() || null,
        is_active: true,
        metadata: {},
      }),
    onSuccess: () => {
      setRefExternalId('');
      setRefCanonicalName('');
      setRefAliasNames('');
      setRefNotes('');
      queryClient.invalidateQueries({ queryKey: ['pitwallWomenReferences'] });
      setFeedbackMessage('Women reference created.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to create women reference.');
    },
  });

  const toggleWomenReference = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin.pitwall.updateWomenReference(id, { is_active: !isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pitwallWomenReferences'] });
      setFeedbackMessage('Women reference status updated.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to update status.');
    },
  });

  const updateWomenReference = useMutation({
    mutationFn: (payload: { id: string; canonical_name: string; alias_names: string[]; notes: string | null }) =>
      api.admin.pitwall.updateWomenReference(payload.id, {
        canonical_name: payload.canonical_name,
        alias_names: payload.alias_names,
        notes: payload.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pitwallWomenReferences'] });
      setEditingReferenceId(null);
      setEditCanonicalName('');
      setEditAliasNames('');
      setEditNotes('');
      setFeedbackMessage('Women reference updated.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to update women reference.');
    },
  });

  const closePhotoEditor = () => {
    setEditingPhotoRacerId(null);
    setPhotoUrlDraft('');
    setPhotoFile(null);
    setPhotoTab('upload');
    setCropImageSrc(null);
    setCropTargetRacerId(null);
  };

  const handleFileSelected = (racerId: string, file: File) => {
    const url = URL.createObjectURL(file);
    setCropImageSrc(url);
    setCropTargetRacerId(racerId);
  };

  const handleCropConfirm = (blob: Blob) => {
    if (!cropTargetRacerId) return;
    const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    uploadRacerPhoto.mutate({ racerId: cropTargetRacerId, file });
    setCropImageSrc(null);
    setCropTargetRacerId(null);
  };

  const updateRacerPhoto = useMutation({
    mutationFn: ({ racerId, photoUrl }: { racerId: string; photoUrl: string | null }) =>
      api.admin.pitwall.updateRacerPhoto(racerId, photoUrl),
    onSuccess: () => {
      closePhotoEditor();
      queryClient.invalidateQueries({ queryKey: ['pitwallRacerSearch'] });
      setFeedbackMessage('Racer photo updated.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to update racer photo.');
    },
  });

  const uploadRacerPhoto = useMutation({
    mutationFn: ({ racerId, file }: { racerId: string; file: File }) =>
      api.admin.pitwall.uploadRacerPhoto(racerId, file),
    onSuccess: () => {
      closePhotoEditor();
      queryClient.invalidateQueries({ queryKey: ['pitwallRacerSearch'] });
      setFeedbackMessage('Racer photo uploaded.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to upload racer photo.');
    },
  });

  const deleteWomenReference = useMutation({
    mutationFn: (id: string) => api.admin.pitwall.deleteWomenReference(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pitwallWomenReferences'] });
      setFeedbackMessage('Women reference deleted.');
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to delete women reference.');
    },
  });

  const {
    data: mergeSearchResponse,
    isFetching: isMergeSearchFetching,
  } = useQuery({
    queryKey: ['pitwallMergeSearch', mergeSearch],
    queryFn: () => api.admin.pitwall.searchRacers(mergeSearch.trim(), { limit: 20 }),
    enabled: mergeSearch.trim().length >= 2,
  });

  const mergeRacers = useMutation({
    mutationFn: () => {
      const sourceIds = mergeQueue.filter(r => r.id !== mergeCanonicalId).map(r => r.id);
      return api.admin.pitwall.mergeRacers(
        mergeCanonicalId!,
        sourceIds,
        mergeNameOverride.trim() || undefined,
      );
    },
    onSuccess: (data: any) => {
      setFeedbackMessage(
        `Merged ${data.merged_count} profile(s) into "${data.canonical_name}" — ${data.results_reassigned} results reassigned, ${data.aliases_created} aliases created.`
      );
      setMergeQueue([]);
      setMergeCanonicalId(null);
      setMergeNameOverride('');
      setMergeConfirming(false);
      queryClient.invalidateQueries({ queryKey: ['pitwallRacerSearch'] });
      queryClient.invalidateQueries({ queryKey: ['pitwallMergeSearch'] });
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Merge failed.');
      setMergeConfirming(false);
    },
  });

  const { data: hiddenRacers, isLoading: isHiddenLoading } = useQuery<HiddenRacer[]>({
    queryKey: ['pitwallHiddenRacers'],
    queryFn: () => api.admin.pitwall.getHiddenRacers(),
  });

  const { data: hideSearchResponse, isFetching: isHideSearchFetching } = useQuery({
    queryKey: ['pitwallHideSearch', hideSearch],
    queryFn: () => api.admin.pitwall.searchRacers(hideSearch.trim(), { limit: 10 }),
    enabled: hideSearch.trim().length >= 2,
  });

  const setRacerHidden = useMutation({
    mutationFn: (payload: { id: string; hidden: boolean }) =>
      api.admin.pitwall.setRacerHidden(payload.id, payload.hidden, payload.hidden ? hideNote.trim() || null : null),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['pitwallHiddenRacers'] });
      queryClient.invalidateQueries({ queryKey: ['pitwallHideSearch'] });
      setFeedbackMessage(data.hidden ? `${data.canonical_name} is hidden from the site.` : `${data.canonical_name} is back on the site.`);
      if (data.hidden) { setHideSearch(''); setHideNote(''); }
    },
    onError: (error: any) => {
      setFeedbackMessage(error?.message || 'Failed to update racer.');
    },
  });

  const startEditingReference = (reference: WomenReference) => {
    setEditingReferenceId(reference.id);
    setEditCanonicalName(reference.canonical_name);
    setEditAliasNames(reference.alias_names.join(', '));
    setEditNotes(reference.notes || '');
  };

  const cancelEditingReference = () => {
    setEditingReferenceId(null);
    setEditCanonicalName('');
    setEditAliasNames('');
    setEditNotes('');
  };

  return (
    <div className="p-6 space-y-5">
      {cropImageSrc && (
        <CropModal
          imageSrc={cropImageSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => { setCropImageSrc(null); setCropTargetRacerId(null); }}
        />
      )}

      {feedbackMessage && (
        <div style={{ borderRadius: '3px', border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.07)', padding: '8px 12px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#C9A84C' }}>
          {feedbackMessage}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '0.06em', color: '#EEEAD5' }}>PitWall Scrape Operations</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => runSmokeChecks.mutate()}
            disabled={runSmokeChecks.isPending}
            style={{ padding: '6px 14px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 600, color: '#888874', background: 'transparent', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '3px', cursor: 'pointer', opacity: runSmokeChecks.isPending ? 0.6 : 1 }}
          >
            {runSmokeChecks.isPending ? 'Checking...' : 'Smoke Checks'}
          </button>
          <button
            onClick={() => bootstrapSources.mutate()}
            style={{ padding: '6px 14px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 600, color: '#888874', background: 'transparent', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '3px', cursor: 'pointer' }}
          >
            Bootstrap Sources
          </button>
        </div>
      </div>

      <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Run Manual Scrape</div>
        <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value as SourceName)}
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px' }}
          >
            {SOURCES.map((source) => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
          <input
            value={eventUrl}
            onChange={(e) => setEventUrl(e.target.value)}
            placeholder="Optional event URL override"
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
            className="md:col-span-2"
          />
          <input
            type="number"
            min={1}
            max={200}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 10)}
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
          />
        </div>
        <button
          onClick={() => runManualScrape.mutate()}
          disabled={runManualScrape.isPending}
          style={{ padding: '8px 18px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 700, color: '#fff', background: '#D93025', border: '1px solid #D93025', borderRadius: '3px', cursor: 'pointer', letterSpacing: '0.04em', opacity: runManualScrape.isPending ? 0.6 : 1 }}
        >
          {runManualScrape.isPending ? 'Running...' : `Run ${selectedSource} Scrape`}
        </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Recent Runs</div>
          <div className="p-3 space-y-1.5">
          {isRunsLoading ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Loading runs...</div>
          ) : runs.length === 0 ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>No runs found for this source.</div>
          ) : (
            runs.map((run: any) => (
              <button
                key={run.run_id}
                onClick={() => setSelectedRunId(run.run_id)}
                className="w-full text-left"
                style={{
                  padding: '10px 12px',
                  background: selectedRunId === run.run_id ? 'rgba(217,48,37,0.08)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${selectedRunId === run.run_id ? 'rgba(217,48,37,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '3px',
                  cursor: 'pointer',
                }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#EEEAD5' }}>{run.source}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: run.status === 'success' ? '#4ade80' : run.status === 'error' ? '#D93025' : '#C9A84C' }}>{run.status}</span>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#6B6B58', marginTop: '3px' }}>{new Date(run.started_at).toLocaleString()}</div>
              </button>
            ))
          )}
          </div>
        </div>

        <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Run Detail</div>
          <div className="p-3">
          {!selectedRunId ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Select a run to inspect details.</div>
          ) : isRunDetailLoading ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Loading run detail...</div>
          ) : runDetail ? (
            <div className="space-y-3">
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: '#888874', lineHeight: 1.7 }}>
                <div>Status: <span style={{ color: runDetail.run.status === 'success' ? '#4ade80' : runDetail.run.status === 'error' ? '#D93025' : '#C9A84C' }}>{runDetail.run.status}</span></div>
                <div>Source: {runDetail.run.source}</div>
                <div>Started: {new Date(runDetail.run.started_at).toLocaleString()}</div>
                <div>Completed: {runDetail.run.completed_at ? new Date(runDetail.run.completed_at).toLocaleString() : 'N/A'}</div>
              </div>
              <div>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '6px' }}>Summary</p>
                <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#888874', background: '#131313', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '3px', padding: '8px', overflow: 'auto' }}>
                  {JSON.stringify(runDetail.run.summary, null, 2)}
                </pre>
              </div>
              <div>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '6px' }}>Documents ({runDetail.documents.length})</p>
                <div className="max-h-48 overflow-auto space-y-1.5">
                  {runDetail.documents.map((doc: any) => (
                    <div key={doc.id} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#888874', background: '#131313', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '3px', padding: '6px 8px' }}>
                      <div style={{ color: '#C9A84C' }} className="truncate">{doc.source_url}</div>
                      <div>Confidence: {doc.extraction_confidence} · Strategy: {doc.parser_strategy || 'N/A'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Unable to load run detail.</div>
          )}
          </div>
        </div>
      </div>

      <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>PitWall Racer Lookup</div>
        <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={racerQuery}
            onChange={(e) => setRacerQuery(e.target.value)}
            placeholder="Search racer name or alias (min 2 chars)"
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
            className="md:col-span-3"
          />
          <select
            value={racerStatusFilter}
            onChange={(e) => setRacerStatusFilter(e.target.value as '' | StatusFilter)}
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px' }}
          >
            {STATUS_FILTERS.map((filter) => (
              <option key={filter.label} value={filter.value}>{filter.label}</option>
            ))}
          </select>
        </div>

        {racerQuery.trim().length < 2 ? (
          <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Type at least 2 characters to search racers.</div>
        ) : isRacerSearchLoading || isRacerSearchFetching ? (
          <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Searching racers...</div>
        ) : !racerSearchResponse?.results?.length ? (
          <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>No racers found.</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-auto">
            {racerSearchResponse.results.map((racer: any) => (
              <div key={racer.racer_id} style={{ padding: '10px 12px', background: '#131313', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' }}>
                <div className="flex items-center justify-between">
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#EEEAD5' }}>{racer.canonical_name}</span>
                  <div className="flex items-center gap-3">
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#888874' }}>Results: {racer.total_results}</span>
                    <button
                      onClick={() => { setEditingPhotoRacerId(racer.racer_id); setPhotoUrlDraft(''); }}
                      style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, color: '#D93025', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Set Photo
                    </button>
                  </div>
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#6B6B58', marginTop: '4px' }}>
                  Car: {racer.car_number || 'N/A'} · Latest: {racer.latest_event_date ? new Date(racer.latest_event_date).toLocaleDateString() : 'N/A'} · DQ: {racer.dq_count} DNF: {racer.dnf_count} DNS: {racer.dns_count}
                </div>
                {editingPhotoRacerId === racer.racer_id && (
                  <div className="mt-2 p-2 space-y-2" style={{ background: '#0A0A0A', border: '1px solid rgba(217,48,37,0.2)', borderRadius: '3px' }}>
                    <div className="flex gap-1">
                      {(['upload', 'url'] as const).map(tab => (
                        <button
                          key={tab}
                          onClick={() => setPhotoTab(tab)}
                          style={{ padding: '4px 10px', fontFamily: "'DM Sans', sans-serif", fontSize: '11px', fontWeight: 600, color: photoTab === tab ? '#fff' : '#888874', background: photoTab === tab ? '#D93025' : 'transparent', border: `1px solid ${photoTab === tab ? '#D93025' : 'rgba(255,255,255,0.08)'}`, borderRadius: '2px', cursor: 'pointer' }}
                        >
                          {tab === 'upload' ? 'Upload File' : 'Paste URL'}
                        </button>
                      ))}
                    </div>
                    {photoTab === 'upload' ? (
                      <div className="flex gap-2 items-center">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelected(racer.racer_id, f); e.target.value = ''; }}
                          className="flex-1 text-xs file:mr-2 file:py-1 file:px-2 file:border-0 file:text-xs file:rounded-sm"
                          style={{ color: '#888874', fontFamily: "'DM Sans', sans-serif" }}
                        />
                        {uploadRacerPhoto.isPending && <span style={{ fontSize: '11px', color: '#C9A84C', fontFamily: "'DM Sans', sans-serif" }}>Uploading…</span>}
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={photoUrlDraft}
                          onChange={(e) => setPhotoUrlDraft(e.target.value)}
                          placeholder="https://… (leave blank to remove)"
                          style={{ flex: 1, background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', padding: '5px 8px', fontSize: '11px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", outline: 'none' }}
                          autoFocus
                        />
                        <button
                          onClick={() => updateRacerPhoto.mutate({ racerId: racer.racer_id, photoUrl: photoUrlDraft || null })}
                          disabled={updateRacerPhoto.isPending}
                          style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#D93025', border: 'none', borderRadius: '2px', cursor: 'pointer' }}
                        >
                          Save
                        </button>
                      </div>
                    )}
                    <button onClick={closePhotoEditor} style={{ fontSize: '11px', color: '#888874', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* ── Racer Merge Tool ────────────────────────────────────────────────── */}
      <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em', borderRadius: '4px 4px 0 0' }}>Merge Racer Profiles</div>
        <div className="p-4 space-y-3">
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#888874' }}>
            Search for racers, add them to the queue, then designate one as canonical. The rest are merged into it and aliases are created automatically.
          </p>

          {/* Search */}
          <div className="relative">
            <input
              value={mergeSearch}
              onChange={(e) => setMergeSearch(e.target.value)}
              placeholder="Search racer name to add to queue..."
              style={{ width: '100%', background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
            />
            {mergeSearch.trim().length >= 2 && mergeSearchResponse?.results?.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '3px', zIndex: 20, maxHeight: '220px', overflowY: 'auto', marginTop: '2px' }}>
                {isMergeSearchFetching && (
                  <div style={{ padding: '8px 12px', fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#888874' }}>Searching...</div>
                )}
                {mergeSearchResponse.results.map((r: any) => {
                  const already = mergeQueue.some(q => q.id === r.racer_id);
                  return (
                    <button
                      key={r.racer_id}
                      disabled={already}
                      onClick={() => {
                        if (!already) {
                          setMergeQueue(prev => [...prev, { id: r.racer_id, name: r.canonical_name }]);
                          setMergeSearch('');
                        }
                      }}
                      className="w-full text-left"
                      style={{ padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: already ? 'default' : 'pointer', opacity: already ? 0.4 : 1 }}
                    >
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#EEEAD5' }}>{r.canonical_name}</span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#6B6B58', marginLeft: '8px' }}>{r.result_count} results</span>
                      {already && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#C9A84C', marginLeft: '8px' }}>already in queue</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Queue */}
          {mergeQueue.length > 0 && (
            <div className="space-y-1.5">
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '6px' }}>
                Merge Queue — click a name to set as canonical (keeper)
              </div>
              {mergeQueue.map((r) => {
                const isCanonical = r.id === mergeCanonicalId;
                return (
                  <div key={r.id} className="flex items-center gap-2"
                    style={{ padding: '8px 12px', background: isCanonical ? 'rgba(74,222,128,0.05)' : 'rgba(255,255,255,0.02)', border: `1px solid ${isCanonical ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: '3px' }}
                  >
                    <button
                      onClick={() => setMergeCanonicalId(r.id)}
                      style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: isCanonical ? 700 : 400, color: isCanonical ? '#4ade80' : '#EEEAD5' }}>{r.name}</span>
                      {isCanonical && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#4ade80', marginLeft: '8px', fontWeight: 600 }}>CANONICAL</span>}
                      {!isCanonical && mergeCanonicalId && <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#888874', marginLeft: '8px' }}>will be merged + deleted</span>}
                    </button>
                    <button
                      onClick={() => {
                        setMergeQueue(prev => prev.filter(q => q.id !== r.id));
                        if (mergeCanonicalId === r.id) setMergeCanonicalId(null);
                      }}
                      style={{ padding: '2px 8px', fontSize: '10px', color: '#888874', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                    >Remove</button>
                  </div>
                );
              })}

              {mergeCanonicalId && mergeQueue.length >= 2 && (
                <div className="pt-2 space-y-2">
                  <input
                    value={mergeNameOverride}
                    onChange={(e) => setMergeNameOverride(e.target.value)}
                    placeholder={`Canonical name override (leave blank to keep "${mergeQueue.find(r => r.id === mergeCanonicalId)?.name}")`}
                    style={{ width: '100%', background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '7px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
                  />
                  {!mergeConfirming ? (
                    <button
                      onClick={() => setMergeConfirming(true)}
                      style={{ padding: '7px 18px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 700, color: '#fff', background: '#D93025', border: '1px solid #D93025', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      Merge {mergeQueue.length - 1} profile(s) into {mergeNameOverride.trim() || mergeQueue.find(r => r.id === mergeCanonicalId)?.name}
                    </button>
                  ) : (
                    <div style={{ padding: '10px 14px', background: 'rgba(217,48,37,0.07)', border: '1px solid rgba(217,48,37,0.25)', borderRadius: '3px' }}>
                      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#EEEAD5', marginBottom: '10px' }}>
                        This will permanently delete {mergeQueue.filter(r => r.id !== mergeCanonicalId).map(r => r.name).join(', ')} and reassign all their results. Are you sure?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => mergeRacers.mutate()}
                          disabled={mergeRacers.isPending}
                          style={{ padding: '6px 16px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 700, color: '#fff', background: '#D93025', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: mergeRacers.isPending ? 0.6 : 1 }}
                        >{mergeRacers.isPending ? 'Merging...' : 'Confirm Merge'}</button>
                        <button
                          onClick={() => setMergeConfirming(false)}
                          style={{ padding: '6px 14px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#888874', background: 'transparent', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '3px', cursor: 'pointer' }}
                        >Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Women Reference Management</div>
        <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={womenSearch}
            onChange={(e) => setWomenSearch(e.target.value)}
            placeholder="Search by name, alias, source, external id"
            style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
            className="md:col-span-3"
          />
          <label className="inline-flex items-center gap-2 px-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#888874', cursor: 'pointer' }}>
            <input type="checkbox" checked={womenOnlyActive} onChange={(e) => setWomenOnlyActive(e.target.checked)} className="h-4 w-4 accent-[#D93025]" />
            Active only
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { val: refSourceSystem, set: setRefSourceSystem, ph: 'source_system (e.g. manual)' },
            { val: refExternalId,   set: setRefExternalId,   ph: 'external_id' },
            { val: refCanonicalName,set: setRefCanonicalName, ph: 'canonical_name' },
            { val: refAliasNames,   set: setRefAliasNames,   ph: 'alias_names (comma separated)' },
          ].map(({ val, set, ph }) => (
            <input key={ph} value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
              style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', outline: 'none' }}
            />
          ))}
          <button
            onClick={() => createWomenReference.mutate()}
            disabled={createWomenReference.isPending || !refExternalId.trim() || !refCanonicalName.trim()}
            style={{ padding: '8px 14px', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 700, color: '#fff', background: '#D93025', border: '1px solid #D93025', borderRadius: '3px', cursor: 'pointer', opacity: (createWomenReference.isPending || !refExternalId.trim() || !refCanonicalName.trim()) ? 0.5 : 1 }}
          >
            Add Reference
          </button>
        </div>

        <input
          value={refNotes}
          onChange={(e) => setRefNotes(e.target.value)}
          placeholder="notes (optional)"
          style={{ width: '100%', background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
        />

        {isWomenReferencesLoading ? (
          <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Loading women references...</div>
        ) : !womenReferences?.length ? (
          <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>No women references created yet.</div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {womenReferences.map((reference) => (
              <div key={reference.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                style={{ padding: '10px 12px', background: '#131313', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' }}
              >
                {editingReferenceId === reference.id ? (
                  <>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                      {[
                        { val: editCanonicalName, set: setEditCanonicalName, ph: 'canonical_name' },
                        { val: editAliasNames,    set: setEditAliasNames,    ph: 'aliases comma-separated' },
                        { val: editNotes,         set: setEditNotes,         ph: 'notes' },
                      ].map(({ val, set, ph }) => (
                        <input key={ph} value={val} onChange={(e) => set(e.target.value)} placeholder={ph}
                          style={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', padding: '6px 8px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '12px', outline: 'none' }}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateWomenReference.mutate({ id: reference.id, canonical_name: editCanonicalName.trim(), alias_names: editAliasNames.split(',').map(n => n.trim()).filter(Boolean), notes: editNotes.trim() || null })}
                        disabled={updateWomenReference.isPending || !editCanonicalName.trim()}
                        style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#D93025', border: 'none', borderRadius: '2px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}
                      >Save</button>
                      <button onClick={cancelEditingReference} style={{ padding: '4px 10px', fontSize: '11px', color: '#888874', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>Cancel</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#EEEAD5' }}>{reference.canonical_name}</div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: '#888874', marginTop: '2px' }}>{reference.source_system} / {reference.external_id}</div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#6B6B58', marginTop: '1px' }}>aliases: {reference.alias_names?.length ? reference.alias_names.join(', ') : 'none'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleWomenReference.mutate({ id: reference.id, isActive: reference.is_active })}
                        disabled={toggleWomenReference.isPending}
                        style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: reference.is_active ? '#4ade80' : '#888874', background: reference.is_active ? 'rgba(74,222,128,0.08)' : 'rgba(255,255,255,0.04)', border: `1px solid ${reference.is_active ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '2px', cursor: 'pointer' }}
                      >{reference.is_active ? 'Active' : 'Inactive'}</button>
                      <button onClick={() => startEditingReference(reference)} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#888874', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => deleteWomenReference.mutate(reference.id)} disabled={deleteWomenReference.isPending} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#D93025', background: 'rgba(217,48,37,0.08)', border: '1px solid rgba(217,48,37,0.2)', borderRadius: '2px', cursor: 'pointer' }}>Delete</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      <div style={{ background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#AEAA94', textTransform: 'uppercase', letterSpacing: '0.2em' }}>Shitlist</div>
        <div className="p-4 space-y-3">
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#888874' }}>
            Racers on this list never show up on the public site: not in search, profiles, event results, the ticker or cards. Their rows are dropped from results tables and other finishers keep their official positions.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={hideSearch}
              onChange={(e) => setHideSearch(e.target.value)}
              placeholder="Find a racer to hide"
              style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
            />
            <input
              value={hideNote}
              onChange={(e) => setHideNote(e.target.value)}
              placeholder="note (optional, admin only)"
              style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '8px 12px', color: '#EEEAD5', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', outline: 'none' }}
            />
          </div>

          {hideSearch.trim().length >= 2 && (
            <div className="space-y-1.5">
              {isHideSearchFetching ? (
                <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Searching...</div>
              ) : !hideSearchResponse?.results?.length ? (
                <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>No visible racer matches. Hidden racers are listed below.</div>
              ) : (
                hideSearchResponse.results.map((r: any) => (
                  <div key={r.racer_id} className="flex items-center justify-between gap-2" style={{ padding: '8px 12px', background: '#131313', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px' }}>
                    <div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#EEEAD5' }}>{r.canonical_name}</div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#6B6B58', marginTop: '1px' }}>{r.total_results} results driving, {r.nav_results} navigating</div>
                    </div>
                    <button
                      onClick={() => setRacerHidden.mutate({ id: r.racer_id, hidden: true })}
                      disabled={setRacerHidden.isPending}
                      style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#D93025', background: 'rgba(217,48,37,0.08)', border: '1px solid rgba(217,48,37,0.2)', borderRadius: '2px', cursor: 'pointer' }}
                    >Hide</button>
                  </div>
                ))
              )}
            </div>
          )}

          {isHiddenLoading ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Loading...</div>
          ) : !hiddenRacers?.length ? (
            <div style={{ color: '#888874', fontSize: '12px', fontFamily: "'DM Sans', sans-serif" }}>Nobody on the list.</div>
          ) : (
            <div className="space-y-1.5">
              {hiddenRacers.map((r) => (
                <div key={r.racer_id} className="flex items-center justify-between gap-2" style={{ padding: '10px 12px', background: '#131313', border: '1px solid rgba(217,48,37,0.2)', borderRadius: '3px' }}>
                  <div>
                    <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 600, color: '#EEEAD5' }}>{r.canonical_name}</div>
                    {r.note && <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', color: '#6B6B58', marginTop: '1px' }}>{r.note}</div>}
                  </div>
                  <button
                    onClick={() => setRacerHidden.mutate({ id: r.racer_id, hidden: false })}
                    disabled={setRacerHidden.isPending}
                    style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, fontFamily: "'DM Sans', sans-serif", color: '#888874', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px', cursor: 'pointer' }}
                  >Unhide</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PitWallAdminPage;
